/**
 * AI Summary Route Guard
 * 
 * Protects the /ai-summary route by checking if the user has access to AI summaries.
 * Redirects to home with a message if they don't have any AI summaries available.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { CircularProgress, Box } from '@mui/material';
import { useAISummaryAvailability } from '../contexts/AISummaryContext';
import { useAuth } from '../auth/AuthContext';

const AISummaryRouteGuard = ({ children }) => {
  const { hasAISummaries, loading: aiLoading, isAdmin } = useAISummaryAvailability();
  const { user, loading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  // Show loading while checking availability
  if (authLoading || aiLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admins always have access
  if (isAdmin) {
    return children;
  }

  // User doesn't have AI summaries - redirect to dashboard
  if (!hasAISummaries) {
    // Show snackbar message once
    setTimeout(() => {
      enqueueSnackbar(
        'AI summaries are not available yet. Contact your administrator for access.',
        { variant: 'info' }
      );
    }, 100);
    
    return <Navigate to="/dashboard" replace />;
  }

  // User has AI summaries - render the route
  return children;
};

export default AISummaryRouteGuard;

